import { TrophyIcon } from '@phosphor-icons/react'
import { useCallback, useEffect, useState } from 'react'

import { isBoggleAchievementsPlayerUiEnabled } from '~/boggle/featureFlags'
import { IconGlyph } from '~/boggle/ui/atoms/Icons'
import {
  BoggleProgressHubModal,
  type BoggleProgressHubTab,
} from '~/boggle/ui/hub/BoggleProgressHubModal'
import { BoggleHubIndicatorActionIcon } from '~/boggle/ui/unviewed/BoggleHubIndicatorActionIcon'
import { useBoggleModeSession } from '~/boggle/ui/state/ModeSession'
import {
  useBoggleUnviewedAchievementTierCount,
  useBoggleUnviewedBadgeButtonCount,
} from '~/boggle/ui/unviewed/useBoggleUnviewedCounts'

const TROPHY_BUTTON_SIZE = 58
const TROPHY_ICON_SIZE = 30

/**
 * Progress hub entry point on home, pause overlay, and results. Opens a tabbed
 * modal (Compendium, Lexicon, Run History) and remembers the last active tab
 * for the current page session.
 */
export function CompendiumTrophyButton({
  testId = 'boggle-compendium-trophy',
}: {
  testId?: string
}) {
  const { isDevMode } = useBoggleModeSession()
  const achievementsPlayerUiEnabled = isBoggleAchievementsPlayerUiEnabled(isDevMode)
  const [opened, setOpened] = useState(false)
  const [activeTab, setActiveTab] = useState<BoggleProgressHubTab>('compendium')
  const { count: unviewedBadgeCount } = useBoggleUnviewedBadgeButtonCount()
  const { count: unviewedAchievementTierCount } = useBoggleUnviewedAchievementTierCount()
  const count =
    unviewedBadgeCount +
    (achievementsPlayerUiEnabled ? unviewedAchievementTierCount : 0)

  useEffect(() => {
    if (!achievementsPlayerUiEnabled && activeTab === 'achievements') {
      setActiveTab('compendium')
    }
  }, [achievementsPlayerUiEnabled, activeTab])
  const open = useCallback(() => {
    setOpened(true)
  }, [])
  const close = useCallback(() => setOpened(false), [])

  return (
    <>
      <BoggleHubIndicatorActionIcon
        aria-label="Open progress hub"
        bd="1px solid rgba(255, 255, 255, 0.18)"
        bdrs={12}
        bg="#171a1a"
        c="rgba(244, 193, 54, 0.95)"
        count={count}
        data-testid={testId}
        h={TROPHY_BUTTON_SIZE}
        indicatorSize={22}
        label="Open progress hub"
        onClick={open}
        variant="light"
        w={TROPHY_BUTTON_SIZE}
      >
        <IconGlyph icon={TrophyIcon} size={TROPHY_ICON_SIZE} weight="duotone" />
      </BoggleHubIndicatorActionIcon>
      <BoggleProgressHubModal
        activeTab={activeTab}
        onClose={close}
        onTabChange={setActiveTab}
        opened={opened}
      />
    </>
  )
}
